import React, { useMemo, useState } from "react";
import { TextField, Typography, Box } from "@mui/material";
import { Controller, useForm, FormProvider } from "react-hook-form";
import useFormFactoryLogic from "@/modules/lead-page/hooks/lead-info/status/form-factory/form-factory-logic.hook";
import { FormFactoryProps } from "@/modules/common/types";
import { StatusType } from "@/modules/common/dtos/index.v2";
import FormItemRenderer from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/form-field-renderer";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import Grid from "@mui/material/GridLegacy";
 import TrackItem from "./track-item";

export const FormFactory: React.FC<FormFactoryProps> = ({
  submitFun,
  formFields,
  isLoadingSubmition,
  handleClose,
  selectedStatus,
  developerTrackConfig = {
    createdTracks: [],
    developers: [],
    isLoadingDevelopers: false,
    setSearchDevelopers: () => {},
    handleAddTrack: () => {},
    handleDeleteTrack: () => {},
    handleTrackChange: () => {},
  },
}) => {
   const {
    createdTracks,
    developers,
    isLoadingDevelopers,
    setSearchDevelopers,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
  } = developerTrackConfig;
  const defaultValues = React.useMemo(() => {
    const values: Record<string, any> = {};
    formFields?.fields?.forEach((field) => {
      const fieldName = field.field.key || field.field.uid;

      if (field.field.defaultValue) {
        if (field.field.type === "dropdown") {
          const defaultValueOption = field.field.dropdowns.find(
            (option) => option.value === field.field.defaultValue
          );
          values[fieldName] = field.isMulti
            ? [
                {
                  label: defaultValueOption.label,
                  value: defaultValueOption.value,
                },
              ]
            : {
                label: defaultValueOption.label,
                value: defaultValueOption.value,
              };
        } else {
          values[fieldName] = field.field.defaultValue;
        }
      } else {
        values[fieldName] = field.isMulti ? [] : null;
      }
    });
    return values;
  }, [formFields]);

  const methods = useForm({
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const { onSubmit, hasCommentTitle } = useFormFactoryLogic({
    submitFun,
    errors,
  });

  const isNoAnswerStatus =
    selectedStatus?.statusType === StatusType.NO_ANSWER;

  const [showValidation, setShowValidation] = useState(false);

  const sortedFields = useMemo(
    () =>
      formFields?.fields?.sort(
        (a, b) =>
          new Date(a.createdAt || "").getTime() -
          new Date(b.createdAt || "").getTime()
      ),
    [formFields?.fields]
  );

  // Check if any tracks are missing a developer (agent is optional)
  const hasIncompleteTrack = useMemo(() => {
    return createdTracks.some((track) => !track.developerUId);
  }, [createdTracks]);

  // Memoize the map of selected developer IDs for each track
  const trackDevelopersMap = useMemo(() => {
    return new Map(
      createdTracks.map(track => [
        track.id,
        createdTracks
          .filter(t => t.id !== track.id)
          .map(t => t.developerUId)
          .filter(Boolean)
      ])
    );
  }, [createdTracks]);

  // Handler for adding a new track with validation
  const handleAddTrackWithValidation = () => {
    if (hasIncompleteTrack) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      handleAddTrack();
    }
  };

  // Reset validation when tracks are updated and all are complete
  React.useEffect(() => {
    if (!hasIncompleteTrack && showValidation) {
      setShowValidation(false);
    }
  }, [hasIncompleteTrack, showValidation]);

  // Wrap onSubmit to validate tracks before submission
  const handleFormSubmit = (data: any) => {
    // If there are any tracks created and they are incomplete, prevent submission
    // This applies regardless of whether createDeveloperTrack is enabled
    if (createdTracks.length > 0 && hasIncompleteTrack) {
      setShowValidation(true);
      return; // Prevent submission
    }
    
    // All validations passed, proceed with submission
    onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Typography variant="h5" gutterBottom>
          {formFields?.name}
        </Typography>
        <Grid container spacing={2} sx={{ paddingTop: "8px" }}>
          {sortedFields?.map((field) => (
            <React.Fragment key={field.id}>
              <FormItemRenderer
                field={field}
                control={control}
                watch={watch}
                setValue={setValue}
              />
            </React.Fragment>
          ))}

          {selectedStatus?.createDeveloperTrack && (
            <Grid item mobile={12}>
              <Box sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
     
                {createdTracks.map((track, index) => {
                   const selectedDeveloperIds = trackDevelopersMap.get(track.id) || [];
                   const availableDevelopers = developers.filter(dev => 
                    !selectedDeveloperIds.includes(dev.uid)
                   );

                   return (
                  <TrackItem
                    key={track.id}
                    track={track}
                    index={index}
                    developers={availableDevelopers}
                    isLoadingDevelopers={isLoadingDevelopers}
                    onSearchDevelopers={setSearchDevelopers}
                    onDelete={handleDeleteTrack}
                    onChange={handleTrackChange}
                    showValidation={showValidation}
                  />
                )})}
                
                <TrackItem
                  isAddButton
                  onAdd={handleAddTrackWithValidation}
                />
              </Box>
            </Grid>
          )}

          {!hasCommentTitle(formFields) && (
            <Grid item mobile={12}>
              <Controller
                name="comment"
                control={control}
                rules={{
                  required: isNoAnswerStatus
                    ? false
                    : "This field is required",
                  minLength: isNoAnswerStatus
                    ? undefined
                    : {
                        value: 4,
                        message: "Minimum 4 characters required",
                      },
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Comment"
                    multiline
                    rows={4}
                    value={value || ""}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ "data-testid": "status-form-comment" }}
                  />
                )}
              />
            </Grid>
          )}
          <FormFooter
            handleClose={handleClose}
            isLoadingSubmition={isLoadingSubmition}
            confirmText="Save"
          />
        </Grid>
      </form>
    </FormProvider>
  );
};
